import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FilterFormValue, filterSchema } from '../schema/filterSchema';
import useStore from '@/lib/store';
import { useReportStore } from '../store/useReportStore';

export default function useDialog() {
  const form = useForm<FilterFormValue>({
    resolver: zodResolver(filterSchema),
  });
  const [open, setOpen] = useState(false);

  const {
    user,
    region,
    district,
    plantsSelect,
    fetchRegion,
    fetchDistrict,
    fetchPlants,
  } = useStore();
  const { actionsByRegionPlan, reportsLoading, fetchActionsByRegionPlan, fetchReports, resetStore } = useReportStore();

  const region_id = form.watch('region_id') || user?.region_id || '';
  const district_id = form.watch('district_id');
  const plant_id = form.watch('plant_id');

  const isRegionDisabled = !!user?.region_id;
  const isDistrictDisabled = !!user?.district_id;

  useEffect(() => {
    if (!open) {
      resetStore();
      const defaults: Partial<FilterFormValue> = {};
      if (user?.region_id) defaults.region_id = user.region_id;
      if (user?.district_id) defaults.district_id = user.district_id;
      form.reset(defaults);
    }
  }, [open]);

  useEffect(() => {
    fetchRegion();
    fetchPlants();
  }, []);

  useEffect(() => {
    if (region_id) {
      fetchDistrict({ region_id });
    }
  }, [region_id]);

  const onPlantChange = (value: string) => {
    const rid = form.getValues('region_id') || user?.region_id || '';
    if (value && rid) {
      fetchActionsByRegionPlan({ plant_id: value, region_id: rid });
    }
  };

  const onRegionChange = (value: string) => {
    const pid = form.getValues('plant_id');
    if (pid && value) {
      fetchActionsByRegionPlan({ plant_id: pid, region_id: value });
    }
  };

  useEffect(() => {
    const defaultValues: Partial<FilterFormValue> = {};
    if (user?.region_id) defaultValues.region_id = user.region_id;
    if (user?.district_id) defaultValues.district_id = user.district_id;
    if (Object.keys(defaultValues).length > 0) {
      form.reset(defaultValues);
    }
  }, [user, form]);

  const selectFormat = useCallback(
    (item: { id: string; name_uz: string }) => ({
      value: item.id,
      label: item.name_uz,
    }),
    [],
  );

  const plantsFormat = useCallback(
    (item: { value: string; label: string }) => ({
      value: item.value,
      label: item.label,
    }),
    [],
  );

  const onSubmit = async (data: FilterFormValue) => {
    const payload = {
      ...data,
      ...(data.action_id?.length ? { action_id: data.action_id } : {}),
    };

    try {
      await fetchReports({ data: payload, setOpen });
      await setOpen(false);
    } catch (error) {}
  };

  return {
    open,
    form,
    region,
    district,
    actions: actionsByRegionPlan,
    user,
    plantsSelect,
    isRegionDisabled,
    isDistrictDisabled,
    reportsLoading,
    setOpen,
    onSubmit,
    onPlantChange,
    onRegionChange,
    selectFormat,
    plantsFormat,
  };
}
